/**
 * pickFile — choose the best browser-playable video file from an archive.org
 * Metadata API response.
 *
 * Algorithm per specs/001-mvp-foundation/research.md R4 (archive-org-expert):
 *
 *   1. Filter files[] to MP4-ish entries (.mp4 extension OR known format tag)
 *   2. Prefer `source: "derivative"` over `"original"`
 *   3. Prefer the largest candidate whose width is <= 1280 (sane ceiling)
 *      (don't default to the absolute largest file)
 *   4. If no MP4 derivative at all: signal embed-fallback mode
 */

import type { MetadataFile, MetadataResponse } from "../../types.ts";

const MP4_FORMAT_RE = /^(h\.?264|mpeg-?4|\d+\s*k?b\s*mpeg4|hires mpeg4|h\.264 hd)$/i;
const MAX_WIDTH = 1280;

export type PickFileResult =
  | { file: MetadataFile; isEmbedFallback: false }
  | { file: null; isEmbedFallback: true };

function isMp4(f: MetadataFile): boolean {
  if (f.name.toLowerCase().endsWith(".mp4")) return true;
  if (f.format && MP4_FORMAT_RE.test(f.format)) return true;
  return false;
}

function intWidth(f: MetadataFile): number {
  const n = f.width ? parseInt(f.width, 10) : NaN;
  return Number.isFinite(n) ? n : 0;
}

function intSize(f: MetadataFile): number {
  const n = f.size ? parseInt(f.size, 10) : NaN;
  return Number.isFinite(n) ? n : 0;
}

export function pickFile(metadata: MetadataResponse): PickFileResult {
  const candidates = metadata.files.filter(isMp4);
  if (candidates.length === 0) {
    return { file: null, isEmbedFallback: true };
  }

  const derivatives = candidates.filter((f) => f.source === "derivative");
  const pool = derivatives.length > 0 ? derivatives : candidates;

  // Prefer files with width <= 1280. Among those, largest by size wins.
  const within = pool.filter((f) => {
    const w = intWidth(f);
    return w > 0 && w <= MAX_WIDTH;
  });

  const rankPool = within.length > 0 ? within : pool;
  const ranked = [...rankPool].sort((a, b) => intSize(b) - intSize(a));
  return { file: ranked[0], isEmbedFallback: false };
}
